import { TableColumn } from '@/types/table';
import { cn } from '@/lib/utils';

export enum SkeletonType {
    CELL = 'cell',
    BADGE = 'badge',
    AVATAR = 'avatar',
    ICON_TEXT = 'icon-text',
    CHECKBOX = 'checkbox',
}

interface TableRowSkeletonProps<TData> {
    columns: TableColumn<TData>[];
    rowCount?: number;
    skeletonTypeMap?: Record<string, SkeletonType>;
}

const getColumnStyles = <T,>(column: TableColumn<T>): React.CSSProperties => ({
    ...(column.width && { width: column.width }),
    ...(column.minWidth && { minWidth: column.minWidth }),
    ...(column.maxWidth && { maxWidth: column.maxWidth }),
});

const SkeletonCell = ({ widthClass = 'w-3/4' }: { widthClass?: string }) => (
    <div className={cn('h-4 skeleton-item rounded', widthClass)} />
);

const SkeletonBadge = () => <div className="h-5 w-16 skeleton-item rounded" />;

const SkeletonAvatar = () => (
    <div className="flex items-center gap-3">
        <div className="w-8 h-8 skeleton-item rounded-full flex-shrink-0" />
        <div className="flex flex-col gap-1.5">
            <div className="h-3.5 w-20 skeleton-item rounded" />
            <div className="h-3 w-24 skeleton-item rounded" />
        </div>
    </div>
);

const SkeletonIconText = () => (
    <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 skeleton-item rounded" />
        <div className="h-4 w-24 skeleton-item rounded" />
    </div>
);

function getSkeletonForColumn<TData>(
    column: TableColumn<TData>,
    colIndex: number,
    skeletonTypeMap?: Record<string, SkeletonType>,
): React.ReactNode {
    const columnId = column.id || String(column.accessor);
    const skeletonType = skeletonTypeMap?.[columnId];

    if (skeletonType) {
        switch (skeletonType) {
            case SkeletonType.BADGE:
                return <SkeletonBadge />;
            case SkeletonType.AVATAR:
                return <SkeletonAvatar />;
            case SkeletonType.ICON_TEXT:
                return <SkeletonIconText />;
            case SkeletonType.CHECKBOX:
                return <div className="h-4 w-4 skeleton-item rounded" />;
            case SkeletonType.CELL:
            default:
                break;
        }
    }

    const widths = ['w-3/4', 'w-2/3', 'w-1/2', 'w-4/5'];
    return <SkeletonCell widthClass={widths[colIndex % widths.length]} />;
}

export function TableRowSkeleton<TData>({
    columns,
    rowCount = 5,
    skeletonTypeMap,
}: TableRowSkeletonProps<TData>) {
    return (
        <tbody>
            {Array.from({ length: rowCount }).map((_, rowIndex) => (
                <tr
                    key={rowIndex}
                    className="border-b border-grey-500 last:border-b-0 skeleton-item-container"
                >
                    {columns.map((column, colIndex) => (
                        <td
                            key={colIndex}
                            style={getColumnStyles(column)}
                            className="px-4 py-3 align-middle"
                        >
                            {getSkeletonForColumn(
                                column,
                                colIndex,
                                skeletonTypeMap,
                            )}
                        </td>
                    ))}
                </tr>
            ))}
        </tbody>
    );
}
